import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./addBooking.module.css";
import { Breadcrumbs, Tabs, Tab, Typography, Box, Snackbar, Alert } from "@mui/material";
import Loader from "../../../components/Loader";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step4 from "./Step4";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import { useParams } from "react-router-dom";
import axios from "axios";

import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import PageHeader from "../../../components/PageHeader";

const STEP_LABELS = ["Parcel Details", "Delivery Address", "Order Summary"];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (<div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box sx={{ pt: { xs: 1, sm: 2 } }}>
        <Typography component="div">{children}</Typography>
      </Box>
    )}
  </div>);
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return { id: `simple-tab-${index}`, "aria-controls": `simple-tabpanel-${index}` };
}

function BookingStepper({ value }) {
  return (
    <div className={styles.Stepper} role="tablist" aria-label="Booking steps">
      {STEP_LABELS.map((label, idx) => {
        const isDone = value > idx;
        const isActive = value === idx;
        return (
          <div
            key={label}
            role="tab"
            aria-selected={isActive}
            aria-current={isActive ? "step" : undefined}
            className={`${styles.StepperItem} ${isActive ? styles.StepperItemActive : ""} ${isDone ? styles.StepperItemDone : ""}`}
          >
            <span className={styles.StepperCircle}>{isDone ? "✓" : idx + 1}</span>
            <span className={styles.StepperLabel}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

const AddBooking = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const { booking_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [bookingData, setBookingData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [selectPCountry, setSelectPCountry] = useState('GB');
  const [customerEmail, setCustomerEmail] = useState('');


  const { websiteData } = useWebsite();
  useSiteBranding("Add delivery booking", websiteData);

  useEffect(() => {
    if (auth?.company && auth?.company?.pickup_country) {
      setSelectPCountry(auth?.company?.pickup_country);
    }
  }, [auth?.company]);

  useEffect(() => {
    const fetchBooking = () => {
      setLoading(true);
      axios.get(process.env.REACT_APP_API_URL + "v1/bookings/" + booking_id, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`
        }
      }).then((response) => {
        setLoading(false);
        let bookingDataTemp = response.data.data;

        setCustomerEmail(bookingDataTemp?.pickup_details?.email);

        let delivery_products = bookingDataTemp?.delivery_products?.map(i => {
          let categoryId = i?.parent_id > 0 ? i?.parent_id : i?.product_id;
          let isItem = i?.parent_id > 0 ? true : false;
          return { id: i?.id, category_id: categoryId, is_item: isItem, item_id: i?.product_id, item_name: i?.product_title, total: i?.sub_amount, quantity: i?.quantity, price: i?.price > 0 ? i?.price : "" };
        });

        let alt_phones_p1 = bookingDataTemp?.pickup_details?.alt_phones?.map(i => {
          return { value: i };
        });

        let alt_phones_r1 = bookingDataTemp?.recipient_details?.alt_phones?.map(i => {
          return { value: i };
        });

        let pickUpData = { address: bookingDataTemp?.pickup_details?.address, customer_id: bookingDataTemp?.pickup_details?.user_id, email: bookingDataTemp?.pickup_details?.email, latitude: parseFloat(bookingDataTemp?.pickup_details?.latitude), longitude: parseFloat(bookingDataTemp?.pickup_details?.longitude), name: bookingDataTemp?.pickup_details?.name, phone: bookingDataTemp?.pickup_details?.phone, post_code: bookingDataTemp?.pickup_details?.post_code, alt_phones: alt_phones_p1 };

        let recipientData = { address: bookingDataTemp?.recipient_details?.address, customer_id: bookingDataTemp?.recipient_details?.user_id, email: bookingDataTemp?.recipient_details?.email, latitude: parseFloat(bookingDataTemp?.recipient_details?.latitude), longitude: parseFloat(bookingDataTemp?.recipient_details?.longitude), name: bookingDataTemp?.recipient_details?.name, phone: bookingDataTemp?.recipient_details?.phone, alt_phones: alt_phones_r1 };

        setBookingData(prev => {
          return { ...prev, customer_id: bookingDataTemp?.customer_id, registered_company: bookingDataTemp?.registered_company, insurance: bookingDataTemp?.insurance, company_name: bookingDataTemp?.company_name, company_shipping: bookingDataTemp?.company_shipping, pickup_date: bookingDataTemp?.pickup_date, notes: bookingDataTemp?.description, pickUpData: pickUpData, recipientData: recipientData, delivery_products: delivery_products, discountVal: bookingDataTemp?.discount_val, discountAmount: bookingDataTemp?.discount_amount };
        })
      }).catch((error) => {
        setLoading(false);
      });
    }

    if (booking_id) {
      fetchBooking();
    }
  }, [booking_id, API_TOKEN]);

  const saveStep1Data = (data) => {
    let bookingDataTemp = bookingData;
    bookingDataTemp = { ...bookingDataTemp, ...data };

    setBookingData(bookingDataTemp);
    setTabValue((prevValue) => prevValue + 1);
  };

  const saveStep2Data = (pickUpData) => {
    let bookingDataTemp = bookingData;
    bookingDataTemp = { ...bookingDataTemp, pickUpData: pickUpData };

    setBookingData(bookingDataTemp);
    setTabValue((prevValue) => prevValue + 1);
  }

  const backToTab = () => {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });

    setTabValue((prevValue) => prevValue - 1);
  };

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Delivery Booking" trail={[{ label: booking_id ? 'Edit' : 'Add' }]} />

      <div className={`${styles.MainCard}`}>
        <Box sx={{ width: "100%" }} className={`${styles.AddBookingTab}`}>
          <BookingStepper value={tabValue} />

          <TabPanel value={tabValue} index={0}>
            <Step1 saveStep1Data={saveStep1Data.bind(this)} bookingData={bookingData} setCustomerEmail={setCustomerEmail.bind(this)} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Step2 saveStep2Data={saveStep2Data.bind(this)} backToTab={backToTab.bind(this)} bookingData={bookingData} selectCountry={selectPCountry} customerEmail={customerEmail} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Step4 booking_id={booking_id} bookingData={bookingData} backToTab={backToTab.bind(this)} setLoading={setLoading.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} />
          </TabPanel>
        </Box>
      </div>
    </div>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(AddBooking);